import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  Chip,
  ThemeIcon,
  Paper,
  Textarea,
  Switch,
  Box,
  Anchor,
  Alert,
  ActionIcon,
  UnstyledButton
} from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconClock, IconCalendarCog, IconSparkles, IconChefHat, IconInfoCircle, IconArrowBackUp, IconX } from '@tabler/icons-react';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../../utils/analytics';
import { useFeatureFlag } from '../../../hooks/useFeatureFlags';

type SpecialConsiderationsStepProps = {
  value: {
    quickMealDays?: string[];
    customRequests?: string;
    usePantryIngredients?: boolean;
    dateRange?: [string | null, string | null];
  };
  onChange: (value: {
    quickMealDays?: string[];
    customRequests?: string;
    usePantryIngredients?: boolean;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  pantryStats?: { staples: number; ingredients: number };
  preferences?: { uniqueMeals?: number };
  embedded?: boolean;
  // Render order/subset of sections (embedded composition can interleave
  // sections from other steps between two instances of this component)
  sections?: Array<'quickDays' | 'pantry' | 'requests'>;
  // The household's most recent request, offered behind a tap. Omitted when
  // editing an existing plan's preferences, where "last week" means nothing.
  lastCustomRequest?: string | null;
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const SpecialConsiderationsStep = ({
  value: considerations,
  onChange: onConsiderationsChange,
  onNext,
  onBack,
  isLastStep,
  pantryStats = { staples: 0, ingredients: 0 },
  preferences,
  embedded = false,
  sections = ['quickDays', 'pantry', 'requests'],
  lastCustomRequest
}: SpecialConsiderationsStepProps) => {
  const posthog = usePostHog();
  // With breakfast/lunch in the plan, these controls are dinner-scoped
  // (quickMealDays, pantry, and custom requests all feed only dinner
  // generation), so their copy must say so. Flag-off users keep the
  // original strings byte-for-byte, and dinners are all they have anyway.
  const additionalMealsEnabled = useFeatureFlag('additional_meals');

  /**
   * The previous request is offered, never pre-filled.
   *
   * Measured across production: of 84 plan-to-plan transitions carrying a
   * request, 47 were rewritten from scratch and 31 were cleared outright, so
   * a prefill would have been right about 7% of the time. Worse than useless
   * in two cases — it resurrects a request the user deliberately dropped, and
   * 13% of requests name a day or occasion ("Tuesday is tacos") that becomes
   * a fabricated constraint in a new week. One tap costs the 93% nothing.
   *
   * Hidden the moment there's anything in the field, so it can never
   * overwrite typing, and dismissable for people who don't want it at all.
   */
  const [offerDismissed, setOfferDismissed] = useState(false);
  const trimmedLastRequest = lastCustomRequest?.trim();
  const showLastRequestOffer =
    !!trimmedLastRequest &&
    !offerDismissed &&
    !considerations.customRequests?.trim();

  // One impression per appearance, not per render: used/shown is the evidence
  // that would justify turning this into a prefill later.
  const offerShownRef = useRef(false);
  useEffect(() => {
    if (showLastRequestOffer && !offerShownRef.current) {
      offerShownRef.current = true;
      posthog?.capture(AnalyticsEvent.LastRequestChipShown, {
        request_length: trimmedLastRequest?.length,
      });
    }
  }, [showLastRequestOffer, trimmedLastRequest, posthog]);

  // Calculate chronologically ordered days with dates based on start date
  const orderedDaysWithDates = useMemo(() => {
    const startDate = considerations.dateRange?.[0];
    if (!startDate) return DAYS_OF_WEEK.map(day => ({ day, date: '' }));

    // Parse date string manually to avoid timezone issues
    const [year, month, day] = startDate.split('-').map(Number);
    const startDateObj = new Date(year, month - 1, day);

    // For date ranges, only show days that are within the selected range
    const endDate = considerations.dateRange?.[1];
    if (endDate) {
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const endDateObj = new Date(endYear, endMonth - 1, endDay);
      const daysDiff = Math.ceil((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      const orderedDays = [];
      for (let i = 0; i < daysDiff; i++) {
        const currentDate = new Date(year, month - 1, day + i);
        const dayOfWeek = currentDate.getDay();
        const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const dateStr = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
        orderedDays.push({
          day: DAYS_OF_WEEK[dayIndex],
          date: dateStr
        });
      }
      return orderedDays;
    }

    // If no end date, show all 7 days starting from start date
    const orderedDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(year, month - 1, day + i);
      const dayOfWeek = currentDate.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const dateStr = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
      orderedDays.push({
        day: DAYS_OF_WEEK[dayIndex],
        date: dateStr
      });
    }

    return orderedDays;
  }, [considerations.dateRange]);

  const orderedDays = useMemo(() => orderedDaysWithDates.map(d => d.day), [orderedDaysWithDates]);


  const updateConsiderations = (updates: Partial<typeof considerations>) => {
    onConsiderationsChange({ ...considerations, ...updates });
  };


  // Inverted logic: We track "take-your-time days" in UI, but store "quick meal days" in preferences
  // Quick meal days = all days EXCEPT take-your-time days
  // We need a way to distinguish "no selection yet" from "all days are take-your-time"
  // Solution: undefined = no selection (all quick), empty array = all take-your-time, array with items = mixed
  const takeYourTimeDays = useMemo(() => {
    const quickDays = considerations.quickMealDays;

    // If undefined, default to all days being quick (no take-your-time days)
    if (quickDays === undefined) return [];

    // If empty array, all days are take-your-time
    if (quickDays.length === 0) return [...orderedDays];

    // Otherwise, take-your-time days are those NOT in quickDays
    return orderedDays.filter(day => !quickDays.includes(day));
  }, [considerations.quickMealDays, orderedDays]);

  const toggleTakeYourTimeDay = (day: string) => {
    const currentQuickDays = considerations.quickMealDays;

    // If undefined (initial state), all days are quick
    if (currentQuickDays === undefined) {
      // Make this day take-your-time by marking all OTHER days as quick
      updateConsiderations({
        quickMealDays: orderedDays.filter(d => d !== day)
      });
      return;
    }

    // If empty array, all days are take-your-time
    if (currentQuickDays.length === 0) {
      // Make this day quick (add it to quick days)
      updateConsiderations({
        quickMealDays: [day]
      });
      return;
    }

    // Mixed state - check if this day is currently quick or take-your-time
    if (currentQuickDays.includes(day)) {
      // Day is currently quick, make it take-your-time (remove from quick days)
      const newQuickDays = currentQuickDays.filter(d => d !== day);
      updateConsiderations({
        quickMealDays: newQuickDays
      });
    } else {
      // Day is currently take-your-time, make it quick (add to quick days)
      updateConsiderations({
        quickMealDays: [...currentQuickDays, day]
      });
    }
  };

  const selectWeekendTakeYourTime = () => {
    const weekdays = orderedDays.filter(day => !['Saturday', 'Sunday'].includes(day));
    // Weekend take-your-time = weekdays are quick
    updateConsiderations({ quickMealDays: weekdays });
  };

  const selectEveryDayTakeYourTime = () => {
    // All days take-your-time = empty quick days array
    updateConsiderations({ quickMealDays: [] });
  };

  const clearSelection = () => {
    // Clear = all quick (no take-your-time days selected)
    updateConsiderations({ quickMealDays: [...orderedDays] });
  };

  const sectionBlocks: Record<'quickDays' | 'pantry' | 'requests', React.ReactNode> = {
    quickDays: (
      <>
      {/* Take-Your-Time Days */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Group gap="md" align="center">
            <ThemeIcon size={32} radius="md" color="green" variant="light">
              <IconClock size={18} />
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Text fw={600} size="lg" c="dark">
                {additionalMealsEnabled ? 'Take-your-time nights' : 'Take-your-time days'}
              </Text>
              <Text size="sm" c="dimmed">
                {additionalMealsEnabled
                  ? 'Most dinners will be easy and hands-off. Pick the nights you want something more involved.'
                  : 'Most days will be easy and hands-off. Pick the days you want something more involved.'}
              </Text>
            </div>
          </Group>

          <Stack gap="sm">
            <Group gap="xs" wrap="wrap">
              {orderedDaysWithDates.map(({ day, date }) => (
                <Chip
                  key={day}
                  checked={takeYourTimeDays.includes(day)}
                  onChange={() => toggleTakeYourTimeDay(day)}
                  variant="outline"
                  color="green"
                  size="md"
                  style={{ minWidth: 'fit-content' }}
                  styles={{
                    label: {
                      paddingTop: 25,
                      paddingBottom: 25
                    }
                  }}
                >
                  <Stack gap={4} align="center">
                    <Text size="xs">{day.slice(0, 3)}</Text>
                    <Text size="9px" opacity={0.5}>{date}</Text>
                  </Stack>
                </Chip>
              ))}
            </Group>

            <Group gap="xs">
              {orderedDays.some(day => ['Saturday', 'Sunday'].includes(day)) && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="green"
                  onClick={selectWeekendTakeYourTime}
                >
                  Weekends
                </Button>
              )}
              {/* Redundant next to a single chip on one-day plans */}
              {orderedDays.length > 1 && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="green"
                  onClick={selectEveryDayTakeYourTime}
                >
                  Every day
                </Button>
              )}
              {takeYourTimeDays.length > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={clearSelection}
                >
                  Clear
                </Button>
              )}
            </Group>
          </Stack>

          {takeYourTimeDays.length > 0 && (
            <Text size="xs" c="green.7">
              {additionalMealsEnabled
                ? `✓ We'll plan more involved dinners for ${takeYourTimeDays.length} night${takeYourTimeDays.length !== 1 ? 's' : ''}`
                : `✓ We'll plan more involved meals for ${takeYourTimeDays.length} day${takeYourTimeDays.length !== 1 ? 's' : ''}`}
            </Text>
          )}
          {takeYourTimeDays.length === 0 && (
            <Text size="xs" c="green.7">
              {additionalMealsEnabled
                ? '✓ Every dinner will keep the effort low'
                : '✓ All days will keep the effort low'}
            </Text>
          )}

          {/* Meal prep note - only show if meal prep is enabled */}
          {preferences?.uniqueMeals !== undefined &&
           considerations.dateRange?.[0] &&
           considerations.dateRange?.[1] &&
           (() => {
             const [startYear, startMonth, startDay] = considerations.dateRange[0].split('-').map(Number);
             const [endYear, endMonth, endDay] = considerations.dateRange[1].split('-').map(Number);
             const start = new Date(startYear, startMonth - 1, startDay);
             const end = new Date(endYear, endMonth - 1, endDay);
             const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
             return preferences.uniqueMeals < totalDays;
           })() && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} radius="lg">
              <Text size="sm">
                {additionalMealsEnabled
                  ? 'Your batch cooks make larger portions, so they can take a little longer.'
                  : 'Meal prep recipes may require more cooking time due to larger batch sizes.'}
              </Text>
            </Alert>
          )}
        </Stack>
      </Paper>
      </>
    ),
    pantry: (
      <>
      {/* Pantry Incorporation - Only show if user has pantry items */}
      {(pantryStats.staples + pantryStats.ingredients > 0) && (
        <Paper
          radius="lg"
          p={{ base: 'lg', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          <Stack gap="md">
            <Group gap="md" align="center">
              <ThemeIcon size={32} radius="md" color="cyan" variant="light">
                <IconChefHat size={18} />
              </ThemeIcon>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} size="lg" c="dark">Use pantry items</Text>
                <Text size="sm" c="dimmed">
                  {additionalMealsEnabled
                    ? 'Incorporate ingredients from your pantry when planning dinners'
                    : 'Incorporate ingredients from your pantry when planning meals'}
                </Text>
                <Anchor href="/pantry" target="_blank" size="xs" c="cyan.6" mt={4}>
                  {pantryStats.staples} staple{pantryStats.staples !== 1 ? 's' : ''} • {pantryStats.ingredients} ingredient{pantryStats.ingredients !== 1 ? 's' : ''}
                </Anchor>
              </Box>
              <Switch
                checked={considerations.usePantryIngredients || false}
                onChange={(event) => updateConsiderations({ usePantryIngredients: event.currentTarget.checked })}
                color="cyan"
                size="md"
                styles={{
                  track: { cursor: 'pointer' },
                  thumb: { cursor: 'pointer' },
                  input: { cursor: 'pointer' }
                }}
              />
            </Group>
            
            {considerations.usePantryIngredients && (
              <Text size="xs" c="green.7">
                ✓ We'll suggest recipes that use ingredients you already have on hand
              </Text>
            )}
          </Stack>
        </Paper>
      )}
      </>
    ),
    requests: (
      <>
      {/* Custom Requests */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Group gap="md" align="center">
            <ThemeIcon size={32} radius="md" color="grape" variant="light">
              <IconSparkles size={18} />
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Text fw={600} size="lg" c="dark">Preferences & requests</Text>
              <Text size="sm" c="dimmed">
                {additionalMealsEnabled
                  ? 'Add any specific preferences or requirements for your dinners'
                  : 'Add any specific preferences or requirements for your meal plan'}
              </Text>
            </div>
          </Group>

          <Textarea
            placeholder={additionalMealsEnabled
              ? 'e.g. Include chicken and beef, no leftovers on Wednesday, extra spicy dishes, low-carb dinners on weekdays...'
              : 'e.g. Include chicken and beef, no leftovers on Wednesday, extra spicy dishes, low-carb meals on weekdays...'}
            value={considerations.customRequests || ''}
            onChange={(e) => updateConsiderations({ customRequests: e.target.value })}
            autosize
            minRows={4}
            maxRows={8}
            size="md"
            maxLength={500}
            styles={{
              input: {
                fontSize: '16px',
                padding: '12px 16px'
              }
            }}
          />
          
          {showLastRequestOffer && (
            <Paper
              radius="md"
              p="sm"
              bg="gray.0"
              style={{ border: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <UnstyledButton
                  onClick={() => {
                    updateConsiderations({ customRequests: trimmedLastRequest });
                    posthog?.capture(AnalyticsEvent.LastRequestChipUsed, {
                      request_length: trimmedLastRequest?.length,
                    });
                  }}
                  style={{ flex: 1, textAlign: 'left' }}
                  aria-label="Use last week's request"
                >
                  <Group gap={6} align="center" mb={4} wrap="nowrap">
                    <IconArrowBackUp size={15} color="var(--mantine-color-grape-6)" />
                    <Text size="sm" fw={600} c="grape.7">
                      Use last week's request
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {trimmedLastRequest}
                  </Text>
                </UnstyledButton>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Dismiss last week's request"
                  onClick={() => {
                    setOfferDismissed(true);
                    posthog?.capture(AnalyticsEvent.LastRequestChipDismissed);
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Paper>
          )}

          {considerations.customRequests && (
            <Text size="xs" c="green.7">
              {additionalMealsEnabled
                ? "✓ We'll incorporate your custom requests into your dinners"
                : "✓ We'll incorporate your custom requests into the meal plan"}
            </Text>
          )}
        </Stack>
      </Paper>
      </>
    ),
  };

  return (
    <Stack
      gap="xl"
      w={embedded ? '100%' : { base: '100%', sm: 700 }}
      mx="auto"
      px={embedded ? 0 : { base: 'xs', sm: 'md' }}
    >
      {/* Header */}
      {!embedded && (
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconCalendarCog size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Meal planning options
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={400}>
              Customize your meal plan preferences
            </Text>
          </Stack>
        </Stack>
      )}



      {sections.map((key) => (
        <Fragment key={key}>{sectionBlocks[key]}</Fragment>
      ))}

      {/* Navigation */}
      {!embedded && (
        <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
          <Button
            variant="subtle"
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>

          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={onNext}
            color="orange"
          >
            {isLastStep ? 'Complete setup' : 'Continue'}
          </Button>
        </Group>
      )}
    </Stack>
  );
};
